import React, { useState } from "react";
import { appClient } from "@/api/appClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Copy, CheckCircle, Database, Package } from "lucide-react";
import { toast } from "sonner";

export default function SetupSupabase() {
  const [sqlScript, setSqlScript] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [setupComplete, setSetupComplete] = useState(false);

  const generateSQL = async () => {
    setIsLoading(true);
    try {
      const response = await appClient.functions.invoke('setupSalesItemsTable', {});
      
      if (response.data.success) {
        setSqlScript(response.data.sql);
        toast.success("SQL script generated successfully!");
      } else {
        toast.error("Failed to generate SQL: " + response.data.error);
      }
    } catch (error) {
      toast.error("Error: " + error.message);
    }
    setIsLoading(false);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(sqlScript);
    toast.success("SQL copied to clipboard!");
  };

  return (
    <div className="bg-slate-50 p-6 md:p-8">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Setup Sales Items Table</h1>
          <p className="text-slate-600">Generate SQL to create your products catalog (cases & services)</p>
        </div>

        <Card className="border-slate-200 shadow-sm mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-600" />
              Sales Items Database
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900">What this creates:</h3>
              <ul className="list-disc list-inside space-y-2 text-slate-700">
                <li><strong>sales_items</strong> table with all necessary columns</li>
                <li><strong>Sample immigration products</strong> (7 case types + 8 services)</li>
                <li><strong>Flexible pricing</strong> (flat or variable rates)</li>
                <li><strong>Configuration system</strong> (person labels, milestones, etc.)</li>
                <li><strong>Cost tracking</strong> (for profit margin calculation)</li>
              </ul>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900">Instructions:</h3>
              <ol className="list-decimal list-inside space-y-2 text-slate-700">
                <li>Click "Generate SQL Script" below</li>
                <li>Copy the generated SQL</li>
                <li>Go to your Supabase project → SQL Editor</li>
                <li>Paste the SQL and click "Run"</li>
                <li>Your sales_items table will be created with sample products</li>
              </ol>
            </div>

            <Button 
              onClick={generateSQL} 
              disabled={isLoading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isLoading ? "Generating..." : "Generate SQL Script"}
            </Button>

            {sqlScript && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-slate-900">Generated SQL:</h3>
                  <Button
                    onClick={copyToClipboard}
                    variant="outline"
                    size="sm"
                    className="gap-2"
                  >
                    <Copy className="w-4 h-4" />
                    Copy SQL
                  </Button>
                </div>

                <div className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto max-h-96 overflow-y-auto">
                  <pre className="text-sm">
                    <code>{sqlScript}</code>
                  </pre>
                </div>

                <div className="flex items-center gap-2 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <p className="text-sm text-blue-900">
                    After running this SQL, you'll have a sales_items table with sample immigration products.
                    You can customize these in Supabase or we'll build a UI for managing them.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-6">
            <h3 className="font-semibold text-blue-900 mb-2">💡 Schema Details:</h3>
            <div className="grid md:grid-cols-2 gap-4 text-sm text-blue-800">
              <div>
                <h4 className="font-semibold mb-1">Core Fields:</h4>
                <ul className="list-disc list-inside space-y-1">
                  <li><strong>item_type:</strong> 'case' or 'service'</li>
                  <li><strong>pricing_type:</strong> 'flat' or 'variable'</li>
                  <li><strong>default_price:</strong> Base price</li>
                  <li><strong>has_cost/default_cost:</strong> Your costs</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-1">Config (JSONB):</h4>
                <ul className="list-disc list-inside space-y-1">
                  <li><strong>person_labels:</strong> Role names</li>
                  <li><strong>milestones:</strong> Default timeline</li>
                  <li><strong>deliverables:</strong> Expected outputs</li>
                  <li>Any custom data you need</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}